import { CircleAlert, CircleCheck, TriangleAlert } from "lucide-react";
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

export type ToastTone = "success" | "warn" | "error";

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  /** 退場アニメーション中。消える直前だけ true になり、終わったら取り除く */
  leaving: boolean;
  /** 同じ文言が連続したときに +1 される。key を変えて入場アニメーションを再生する */
  nonce: number;
}

/** 表示時間。失敗は読み終える前に消えると困るので長めにする */
const SHOW_MS: Record<ToastTone, number> = {
  success: 3500,
  warn: 5000,
  error: 8000,
};

/** 退場アニメーション(index.css の toast-out)の長さ */
const LEAVE_MS = 200;

type Show = (message: string, tone?: ToastTone) => void;

const ToastContext = createContext<Show | null>(null);

/** 一時的な通知を出す。完了・失敗など「その場限りの出来事」に使う */
export function useToast(): Show {
  const show = useContext(ToastContext);
  if (show === null) throw new Error("ToastProvider の外では使えません");
  return show;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // 最新の一覧を同期的に読むための鏡。表示の更新は commit で必ず両方に流す
  const listRef = useRef<Toast[]>([]);
  const timers = useRef(new Map<number, number>());
  const nextId = useRef(0);

  const commit = useCallback((next: Toast[]) => {
    listRef.current = next;
    setToasts(next);
  }, []);

  const remove = useCallback(
    (id: number) => {
      timers.current.delete(id);
      commit(listRef.current.filter((toast) => toast.id !== id));
    },
    [commit],
  );

  const dismiss = useCallback(
    (id: number) => {
      const timer = timers.current.get(id);
      if (timer !== undefined) clearTimeout(timer);
      timers.current.delete(id);
      commit(
        listRef.current.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)),
      );
      window.setTimeout(() => {
        remove(id);
      }, LEAVE_MS);
    },
    [commit, remove],
  );

  const show = useCallback<Show>(
    (message, tone = "success") => {
      // 同じ文言が出ている間は積み増やさない(並べ替えの連打などで山にならないように)。
      // 表示時間を延長し、入場アニメーションだけ再生して「効いた」ことを伝える
      const existing = listRef.current.find(
        (toast) => toast.message === message && toast.tone === tone && !toast.leaving,
      );
      if (existing !== undefined) {
        const timer = timers.current.get(existing.id);
        if (timer !== undefined) clearTimeout(timer);
        timers.current.set(
          existing.id,
          window.setTimeout(() => {
            dismiss(existing.id);
          }, SHOW_MS[tone]),
        );
        commit(
          listRef.current.map((toast) =>
            toast.id === existing.id ? { ...toast, nonce: toast.nonce + 1 } : toast,
          ),
        );
        return;
      }

      const id = nextId.current;
      nextId.current += 1;
      timers.current.set(
        id,
        window.setTimeout(() => {
          dismiss(id);
        }, SHOW_MS[tone]),
      );
      commit([...listRef.current, { id, message, tone, leaving: false, nonce: 0 }]);
    },
    [commit, dismiss],
  );

  return (
    <ToastContext.Provider value={show}>
      {children}
      {/*
        全画面表示の詳細やゴミ箱(z-50)より上に出す。
        読み上げのため、入れ替わる中身ではなく居続ける入れ物に aria-live を置く。
      */}
      <div
        aria-live="polite"
        // xl 未満は全幅になり右上のベル・メニューに被るため、ヘッダーの高さぶん
        // 下げて直下に出す(3.75rem ≒ ScreenHeader の高さ)。PC は中央上端のまま
        className="pointer-events-none fixed inset-x-0 top-[calc(3.75rem+env(safe-area-inset-top))] z-60 mx-auto flex max-w-[var(--toast-w)] flex-col gap-2 p-3 xl:top-0 xl:pt-[calc(0.75rem+env(safe-area-inset-top))]"
      >
        {toasts.map((toast) => (
          <button
            key={`${String(toast.id)}:${String(toast.nonce)}`}
            type="button"
            onClick={() => {
              if (!toast.leaving) dismiss(toast.id);
            }}
            className={`pointer-events-auto flex items-start gap-2 rounded-xl px-4 py-3 text-left text-sm whitespace-pre-line text-white shadow-lg ${
              toast.leaving ? "toast-out" : "toast-in"
            } ${
              toast.tone === "error"
                ? "bg-[var(--color-plum)]"
                : toast.tone === "warn"
                  ? "bg-[var(--color-warn)]"
                  : "bg-[var(--color-accent)]"
            }`}
          >
            {toast.tone === "error" ? (
              <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            ) : toast.tone === "warn" ? (
              <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            ) : (
              <CircleCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
            )}
            <span className="flex-1">{toast.message}</span>
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
